import React, { useEffect, useMemo, useCallback, useRef } from 'react';
import Form from '../../../common/Form';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { closeModalAction } from '../../../actions/modal';
import { useSelector } from 'react-redux';
import Select from '../../../common/Form/Select';
import { actualValueForDropdown, debounce, validateStringValue } from '../../../utils/common';
import { getProjectPmsList, postProjectManager } from '../../../actions/project';
import { CLEAR_PROJECT_ELIGIBLE_PMS_LIST } from '../../../types/project';

const formName = 'editProjectPm';

export const getList = (list, pm1, pm2) => {
    let selected = [];
    if (pm1) selected.push(pm1.value);
    if (pm2) selected.push(pm2.value);
    return list.filter((item) => !selected.includes(item.value));
}

export default function EditProjectPM({ projectId, first_pm, second_pm, third_pm, escalate_manager, org_id }) {
    const dispatch = useDispatch();
    const { formButtonLoading, isUserListLoading, isProjectPmListLoading } = useSelector((state) => state.loading);
    const eligiblePms = useSelector((state) => state.project.eligiblePms);
    const defaultValues = {};
    const methods = useForm({ defaultValues, mode: 'all' });
    const { watch, setValue } = methods;
    const project_manager_1 = watch('first_pm_id');
    const project_manager_2 = watch('second_pm_id');
    const project_manager_3 = watch('third_pm_id');
    const fetchedRef = useRef(false);

    const getPmList = useCallback(({ limit, page, query, org_id }) => {
        dispatch(getProjectPmsList({ can_manage_projects: true, limit, page, query, org_id }))
    }, [dispatch]);

    const debouncedGetPmList = useCallback(debounce(getPmList), []);

    useEffect(() => {
        getPmList({ limit: 100, page: 1, org_id });
        return () => {
            dispatch({ type: CLEAR_PROJECT_ELIGIBLE_PMS_LIST })
        }
    }, [dispatch, getPmList, org_id]);

    useEffect(() => {
        if (eligiblePms && eligiblePms.length > 0 && !fetchedRef.current) {
            const pmConfigs = [
                { pm: first_pm, field: 'first_pm_id' },
                { pm: second_pm, field: 'second_pm_id' },
                { pm: third_pm, field: 'third_pm_id' },
                { pm: escalate_manager, field: 'escalate_manager' }
            ];
            pmConfigs.forEach(({ pm, field }) => {
                if (pm) {
                    const findPm = eligiblePms.find((p) => p.id === pm.id);
                    if (findPm) {
                        setValue(field, findPm);
                    }
                }
            });
            fetchedRef.current = true;
        }
    }, [setValue, first_pm, second_pm, third_pm, escalate_manager, eligiblePms]);

    const submit = (form) => {
        const { first_pm_id, second_pm_id, third_pm_id, escalate_manager } = form;

        const managers = [
            {
                user_id: actualValueForDropdown(first_pm_id),
                role: "PM1"
            },
            {
                user_id: actualValueForDropdown(second_pm_id),
                role: "PM2"
            },
            {
                user_id: actualValueForDropdown(third_pm_id),
                role: "PM3"
            },
            {
                user_id: actualValueForDropdown(escalate_manager),
                role: "EM"
            }
        ].filter(manager => manager.user_id);

        const payload = {
            managers
        };
        dispatch(postProjectManager({ payload, projectId, formName }));
    }

    return <Form
        methods={methods}
        onSubmit={submit}
        submitButtonText={'Submit'}
        onCancel={() => dispatch(closeModalAction())}
        showSubmit
        submitContainerClassName='flex gap-2 justify-end py-4 px-4'
        isLoading={formButtonLoading[formName]}
    >
        <div className='px-3 grid grid-cols-4 rows-fr grid-flow-row-dense'>
            <Select onInputChange={(event) => {
                if (event.length > 0) {
                    debouncedGetPmList({ query: event, limit: 100, page: 1, org_id })
                }
            }} isLoading={isProjectPmListLoading} label='PM 1' name='first_pm_id' options={getList(eligiblePms, project_manager_2, project_manager_3)} />
            <Select onInputChange={(event) => {
                if (event.length > 0) {
                    debouncedGetPmList({ query: event, limit: 100, page: 1, org_id })
                }
            }} isLoading={isProjectPmListLoading} label='PM 2' name='second_pm_id' options={getList(eligiblePms, project_manager_1, project_manager_3)} readOnly={!project_manager_1} />
            <Select onInputChange={(event) => {
                if (event.length > 0) {
                    debouncedGetPmList({ query: event, limit: 100, page: 1, org_id })
                }
            }} isLoading={isProjectPmListLoading} label='PM 3' name='third_pm_id' options={getList(eligiblePms, project_manager_1, project_manager_2)} readOnly={!project_manager_1 || !project_manager_2} />
            <Select onInputChange={(event) => {
                if (event.length > 0) {
                    debouncedGetPmList({ query: event, limit: 100, page: 1, org_id })
                }
            }} isLoading={isProjectPmListLoading} label='EM' name='escalate_manager' options={eligiblePms} />
        </div>
    </Form>
}